import isNil from "lodash/isNil";

/**
 * A session record shaped as `{ record: { data: ... } }`, as returned by SessionData.getDataByType.
 */
type RecordLike = { record?: { data?: any } } | undefined;

/**
 * A rule that compares two session record fields for equality.
 * Both references use the "<recordType>.<fieldPath>" form (the field path may be nested with dots).
 */
export interface RecordMatchRule {
	record: string;
	match: string;
}

/**
 * Parses a "<recordType>.<fieldPath>" reference into its record type and (possibly nested) field path.
 */
export const parseRecordRef = (ref: string): { type: string; path: string[] } => {
	const [type, ...path] = ref.split(".");
	return { type, path };
};

/**
 * Resolves a (possibly nested) field value from a session record's `record.data`.
 */
export const resolveFieldValue = (record: RecordLike, path: string[]): any => {
	return path.reduce<any>((value, key) => value?.[key], record?.record?.data);
};

/**
 * Evaluates a {@link RecordMatchRule} by resolving both referenced record fields and comparing them.
 *
 * @param rule The rule to evaluate.
 * @param getRecord Resolver that fetches a session record by its type.
 * @returns `true` when both values are defined and strictly equal, otherwise `false`.
 */
export const evaluateRecordMatchRule = async (
	rule: RecordMatchRule,
	getRecord: (type: string) => Promise<RecordLike>
): Promise<boolean> => {
	const recordRef = parseRecordRef(rule.record);
	const matchRef = parseRecordRef(rule.match);

	const [recordData, matchData] = await Promise.all([getRecord(recordRef.type), getRecord(matchRef.type)]);

	const recordValue = resolveFieldValue(recordData, recordRef.path);
	const matchValue = resolveFieldValue(matchData, matchRef.path);

	return !isNil(recordValue) && !isNil(matchValue) && recordValue === matchValue;
};
